import React, { useEffect, useState } from "react";
import { Map, Star, Filter } from "lucide-react";
import { getHijamaPoints } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import AtlasBookCard from "../components/AtlasBookCard";
import HijamaSunnahDaysCard from "../components/HijamaSunnahDaysCard";

const HijamaAtlas = () => {
  const [points, setPoints] = useState([]);
  const [filter, setFilter] = useState("All");
  const [sunnahOnly, setSunnahOnly] = useState(false);
  const [query, setQuery] = useState("");
  const { t, lang } = useLanguage();

  useEffect(() => {
    getHijamaPoints(null, lang).then((d) => setPoints(d.points || []));
  }, [lang]);

  const regions = ["All", ...Array.from(new Set(points.map((p) => p.region)))];

  const filtered = points.filter((p) => {
    if (filter !== "All" && p.region !== filter) return false;
    if (sunnahOnly && !p.is_sunnah) return false;
    if (
      query &&
      !(
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.arabic.includes(query) ||
        p.indications.some((i) => i.toLowerCase().includes(query.toLowerCase()))
      )
    )
      return false;
    return true;
  });

  return (
    <div data-testid="hijama-atlas-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <div className="mb-10 max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-forest/5 text-forest rounded-full px-4 py-1.5 text-xs mb-5">
          <Map className="w-3.5 h-3.5" />
          {t("atlas_pill")}
        </div>
        <p className="arabic text-gold text-2xl mb-3">أطلس الحجامة</p>
        <h1 data-testid="atlas-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          {t("atlas_title")}
        </h1>
        <p className="text-mute leading-relaxed text-lg">
          {t("atlas_subtitle_1")} <em>{t("atlas_subtitle_2")}</em>{t("atlas_subtitle_3")} <em>{t("atlas_subtitle_4")}</em>{t("atlas_subtitle_5")}
        </p>
      </div>

      {/* Featured: complete Atlas book by Sheikh Ahmed Al-Hefni */}
      <div className="mb-10">
        <AtlasBookCard />
      </div>

      {/* Hijama Sunnah Days reminder + WhatsApp booking */}
      <div className="mb-10">
        <HijamaSunnahDaysCard />
      </div>

      <div data-testid="atlas-filters" className="clay-surface rounded-3xl p-5 mb-8 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-forest" />
        <select
          data-testid="region-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-cream border border-forest/10 rounded-full px-4 py-2 text-sm text-ink outline-none focus:border-forest"
        >
          {regions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        <label data-testid="sunnah-filter-label" className="flex items-center gap-2 text-sm text-mute cursor-pointer">
          <input
            data-testid="sunnah-filter-checkbox"
            type="checkbox"
            checked={sunnahOnly}
            onChange={(e) => setSunnahOnly(e.target.checked)}
            className="rounded accent-forest"
          />
          {t("sunnah_only")}
        </label>

        <input
          data-testid="atlas-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("atlas_search_placeholder")}
          className="flex-1 min-w-[200px] bg-cream border border-forest/10 rounded-full px-5 py-2 text-sm outline-none focus:border-forest"
        />

        <span className="text-xs text-mute ml-auto">
          {filtered.length} {t("points_of")} {points.length} {t("points")}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <article
            key={p.id}
            id={`point-${p.id}`}
            data-testid={`atlas-point-${p.id}`}
            className="clay-surface rounded-2xl p-6 hover:-translate-y-1 transition-transform"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="w-10 h-10 rounded-full bg-forest text-sand flex items-center justify-center text-sm font-semibold" data-testid={`point-id-${p.id}`}>
                {p.id}
              </span>
              {p.is_sunnah && (
                <Star className="w-5 h-5 text-gold" fill="currentColor" data-testid={`sunnah-icon-${p.id}`} />
              )}
            </div>
            <h3 className="font-heading text-lg text-forest font-semibold leading-snug mb-1">
              {lang === "ar" ? p.arabic : p.name}
            </h3>
            <p className="arabic text-gold text-base mb-3">{p.arabic}</p>
            <p className="text-xs text-mute mb-2">
              <strong className="text-ink">{t("region")}</strong> {p.region}
            </p>
            <p className="text-xs text-mute mb-3">
              <strong className="text-ink">{t("location")}</strong> {p.location}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {p.indications?.slice(0, 4).map((ind) => (
                <span key={ind} className="bg-cream text-forest text-[10px] px-2 py-1 rounded-full">{ind}</span>
              ))}
            </div>
            {p.hadith_ref && (
              <p className="text-[10px] text-forest italic border-t border-forest/10 pt-2">{p.hadith_ref}</p>
            )}
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-mute">{t("no_points_match")}</div>
      )}
    </div>
  );
};

export default HijamaAtlas;
